/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import type { DesktopRouteState, DesktopSessionState } from '@poweralpha/common';
import {
  getDefaultWindowMetrics,
  isMaximizedByDefault,
  MIN_FIT_WIDTH,
  MIN_WINDOW_HEIGHT,
  MIN_WINDOW_WIDTH,
} from './windowDefaults';
import { fitWindowsToBounds } from './windowFit';

export { getDefaultWindowMetrics } from './windowDefaults';

import { resolveDesktopWindowPath } from './desktopAppRouting';
import { getAppPathSpecificity, resolveAppForPath } from './navigation/appPaths';
import type { DesktopApp, DesktopState, DesktopWindowState } from './types';

export type { DesktopState, DesktopTabState, DesktopWindowState } from './types';

import { createHistory, createHistoryEntry, MAX_WINDOW_HISTORY_ENTRIES } from './windowHistory';

export {
  createHistory,
  type DesktopWindowHistory,
  type DesktopWindowHistoryEntry,
  getCurrentHistoryEntry,
  MAX_WINDOW_HISTORY_ENTRIES,
  restoreHistory,
} from './windowHistory';

import { createDesktopStateFromSession } from './windowSession';
import { applyTabAction, getWindowTabs, syncSelectedTab } from './windowTabs';

export { serializeDesktopState } from './windowSession';

import { setSplitWeights, type TileDropTarget } from './tileTree';
import {
  addTile,
  canDropWindow,
  type DesktopBounds,
  dropWindow,
  isTiledWindow,
  reflowTiles,
  toggleFullScreen,
  untile,
} from './windowTiling';
export type DesktopWindowAction<RouteState extends DesktopRouteState = DesktopRouteState> =
  | { type: 'hydrate'; desktopState: DesktopSessionState<RouteState>; apps: readonly DesktopApp[] }
  | { type: 'open'; id: string; path?: string; state?: unknown; replace?: boolean }
  | {
      type: 'navigate';
      id: string;
      path: string;
      state?: unknown;
      replace?: boolean;
      tabId?: string;
    }
  | { type: 'go'; id: string; delta: number; tabId?: string }
  | {
      type: 'open_path';
      apps: readonly DesktopApp[];
      path: string;
      replace?: boolean;
      sourceWindowId?: string;
      sourceTabId?: string;
      state?: unknown;
      /** Project that windows at their app's default path follow, so their tabs match its URL. */
      selectedProjectId?: string | null;
    }
  | { type: 'create_tab'; id: string; path: string; tabId?: string; state?: unknown }
  | { type: 'select_tab'; id: string; tabId: string }
  | { type: 'close_tab'; id: string; tabId: string }
  /** Green traffic light. */
  | { type: 'toggle_full_screen'; id: string }
  | { type: 'choose_tile'; id: string }
  | { type: 'dismiss_tile_picker' }
  /** A window drag ended: snapped to `target`, or left floating at `x`/`y`. */
  | { type: 'drop_window'; id: string; x: number; y: number; target?: TileDropTarget }
  | { type: 'resize_tiles'; path: number[]; weights: number[] }
  | { type: 'set_maximized'; id: string; maximized: boolean }
  | { type: 'layout_bounds'; bounds: DesktopBounds }
  | { type: 'focus'; id: string }
  | { type: 'close'; id: string }
  | { type: 'minimize'; id: string }
  | { type: 'toggle_maximize'; id: string }
  | { type: 'update_route_state'; id: string; routeState: RouteState | undefined; tabId?: string }
  | { type: 'move'; id: string; x: number; y: number }
  | { type: 'resize'; id: string; width: number; height: number };

export const createInitialDesktopState = (apps: readonly DesktopApp[]): DesktopState => {
  const now = new Date().toISOString();
  return {
    windows: apps.map((app, index) => ({
      id: app.id,
      appId: app.id,
      title: app.label,
      path: app.defaultPath,
      history: createHistory(app.defaultPath),
      ...getDefaultWindowMetrics(app.id, apps),
      zIndex: index + 1,
      // The desktop starts on the home page; apps open from the dock or a deep link.
      isOpen: false,
      isMinimized: false,
      isMaximized: isMaximizedByDefault(app.id),
      createdAt: now,
      updatedAt: now,
    })),
    focusedWindowId: undefined,
    nextZIndex: apps.length + 1,
  };
};

export const desktopReducer = <RouteState extends DesktopRouteState = DesktopRouteState>(
  state: DesktopState<RouteState>,
  action: DesktopWindowAction<RouteState>,
): DesktopState<RouteState> => {
  // A snap that no longer fits (the desktop changed mid-drag) leaves the window floating.
  if (
    action.type === 'drop_window' &&
    action.target &&
    !canDropWindow(state, action.id, action.target)
  )
    return desktopReducer(state, { ...action, target: undefined });
  let base = state;
  const tiled = 'id' in action && isTiledWindow(state, action.id);
  // Moving or resizing a tile by hand (or by the Agent) frees it where it is; a window dragged
  // out of its tile, or maximized from it, returns to its own size.
  if (tiled && (action.type === 'move' || action.type === 'resize'))
    base = untile(state, action.id, { keepBounds: true });
  if (
    tiled &&
    (action.type === 'toggle_maximize' ||
      action.type === 'set_maximized' ||
      (action.type === 'drop_window' && !action.target))
  )
    base = reflowTiles(untile(state, action.id, { keepSlot: action.type !== 'drop_window' }));
  const next = reduceDesktop(base, action);
  // An app opened (or brought forward from the dock) beside a free slot fills it.
  return reflowTiles(action.type === 'open' ? addTile(next, action.id) : next);
};

const reduceDesktop = <RouteState extends DesktopRouteState = DesktopRouteState>(
  state: DesktopState<RouteState>,
  action: DesktopWindowAction<RouteState>,
): DesktopState<RouteState> => {
  switch (action.type) {
    case 'hydrate': {
      // The session can arrive after the desktop was measured: fit it to those bounds too.
      const hydrated = {
        ...createDesktopStateFromSession(action.desktopState, action.apps),
        availableBounds: state.availableBounds,
      };
      return state.availableBounds && state.availableBounds.width >= MIN_FIT_WIDTH
        ? fitWindowsToBounds(hydrated, state.availableBounds)
        : hydrated;
    }
    case 'create_tab':
    case 'select_tab':
    case 'close_tab':
      return applyTabAction(state, action);
    case 'choose_tile':
      return addTile(state, action.id);
    case 'dismiss_tile_picker':
      // The slot stays free; the next app opened takes it.
      return { ...state, tilePickerSlotId: undefined };
    case 'drop_window':
      if (action.target) return dropWindow(state, action.id, action.target);
      return reduceDesktop(state, { type: 'move', id: action.id, x: action.x, y: action.y });
    case 'resize_tiles':
      return state.tileLayout
        ? { ...state, tileLayout: setSplitWeights(state.tileLayout, action.path, action.weights) }
        : state;
    case 'set_maximized':
      return focusWindow(state, action.id, {
        isMaximized: action.maximized,
        updatedAt: new Date().toISOString(),
      });
    case 'toggle_full_screen':
      return toggleFullScreen(state, action.id);
    case 'layout_bounds':
      if (
        state.availableBounds?.width === action.bounds.width &&
        state.availableBounds?.height === action.bounds.height
      )
        return state;
      // A desktop this narrow is covered, not resized (or not laid out yet): fitting windows to
      // it would shrink them for good, since wider bounds never grow them back.
      if (action.bounds.width < MIN_FIT_WIDTH) return { ...state, availableBounds: action.bounds };
      return fitWindowsToBounds({ ...state, availableBounds: action.bounds }, action.bounds);
    case 'open':
      return openWindow(state, action);
    case 'navigate':
      return navigateWindow(state, action);
    case 'go':
      return goWindow(state, action.id, action.delta, action.tabId);
    case 'open_path':
      return openPath(state, action);
    case 'focus': {
      if (state.focusedWindowId === action.id) {
        return state;
      }
      return focusWindow(state, action.id, {
        updatedAt: new Date().toISOString(),
      });
    }
    case 'close':
      return updateWindow(state, action.id, {
        isOpen: false,
        isMinimized: false,
        updatedAt: new Date().toISOString(),
      });
    case 'minimize':
      return updateWindow(state, action.id, {
        isMinimized: true,
        updatedAt: new Date().toISOString(),
      });
    case 'toggle_maximize':
      return toggleMaximize(state, action.id);
    case 'update_route_state':
      return updateWindow(
        state,
        action.id,
        {
          routeState: action.routeState,
          updatedAt: new Date().toISOString(),
        },
        action.tabId,
      );
    case 'move':
      return updateWindow(state, action.id, {
        x: Math.round(action.x),
        y: Math.round(action.y),
        isMaximized: false,
        updatedAt: new Date().toISOString(),
      });
    case 'resize':
      return updateWindow(state, action.id, {
        width: Math.max(MIN_WINDOW_WIDTH, Math.round(action.width)),
        height: Math.max(MIN_WINDOW_HEIGHT, Math.round(action.height)),
        isMaximized: false,
        updatedAt: new Date().toISOString(),
      });
    default:
      return state;
  }
};

const openWindow = <RouteState extends DesktopRouteState = DesktopRouteState>(
  state: DesktopState<RouteState>,
  {
    id,
    path,
    replace,
    state: locationState,
  }: { id: string; path?: string; state?: unknown; replace?: boolean },
): DesktopState<RouteState> => {
  const target = state.windows.find((windowState) => windowState.id === id);
  if (!target) {
    return state;
  }

  let locationUpdates: Partial<DesktopWindowState<RouteState>> = {};
  if (path !== undefined) {
    // A closed window starts a fresh navigation stack; an open one keeps its back history.
    locationUpdates = target.isOpen
      ? getNavigationUpdates(target, path, locationState, replace)
      : { path, history: createHistory(path, locationState) };
  }

  return focusWindow(state, id, {
    ...locationUpdates,
    isOpen: true,
    isMinimized: false,
    isMaximized: target.isOpen ? target.isMaximized : isMaximizedByDefault(target.id),
    updatedAt: new Date().toISOString(),
  });
};

/** Window that shows pages of `app`. One per app for now; the single place to change for multi-window apps. */
export const getWindowIdForApp = (app: Pick<DesktopApp, 'id'>) => app.id;

const openPath = <RouteState extends DesktopRouteState = DesktopRouteState>(
  state: DesktopState<RouteState>,
  {
    apps,
    path,
    replace,
    sourceWindowId,
    sourceTabId,
    state: locationState,
    selectedProjectId = null,
  }: Extract<DesktopWindowAction<RouteState>, { type: 'open_path' }>,
): DesktopState<RouteState> => {
  const source = state.windows.find((windowState) => windowState.id === sourceWindowId);
  const sourceApp = apps.find((app) => app.id === source?.appId);
  const targetApp = resolveAppForPath(apps, path);

  // Stay in the source window unless another app claims the path more specifically.
  if (
    source &&
    sourceApp &&
    (!targetApp || getAppPathSpecificity(sourceApp, path) >= getAppPathSpecificity(targetApp, path))
  ) {
    return navigateWindow(state, {
      id: source.id,
      path,
      replace,
      state: locationState,
      tabId: sourceTabId,
    });
  }
  if (!targetApp) {
    return state;
  }

  const target = state.windows.find(({ id }) => id === getWindowIdForApp(targetApp));
  if (target?.isOpen) {
    const tabs = getWindowTabs(target);
    // A tab following the selected project shows (and puts in the URL) its resolved path.
    const showsPath = (tab: { path: string }) =>
      resolveDesktopWindowPath({ app: targetApp, path: tab.path, selectedProjectId }) === path;
    const matching =
      tabs.find((tab) => tab.id === target.selectedTabId && showsPath(tab)) ?? tabs.find(showsPath);
    const next = matching
      ? applyTabAction(state, { type: 'select_tab', id: target.id, tabId: matching.id })
      : applyTabAction(state, { type: 'create_tab', id: target.id, path, state: locationState });
    const withLocationState =
      matching && locationState !== undefined
        ? navigateWindow(next, {
            id: target.id,
            path,
            state: locationState,
            replace: true,
            tabId: matching.id,
          })
        : next;
    return focusWindow(withLocationState, target.id, { isMinimized: false });
  }
  return openWindow(state, {
    id: getWindowIdForApp(targetApp),
    path,
    replace,
    state: locationState,
  });
};

const getNavigationUpdates = <RouteState extends DesktopRouteState = DesktopRouteState>(
  target: Pick<DesktopWindowState<RouteState>, 'path' | 'history'>,
  path: string,
  locationState: unknown,
  replace: boolean | undefined,
): Pick<DesktopWindowState<RouteState>, 'history' | 'path'> => {
  const { entries, index } = target.history;
  const entry = createHistoryEntry(path, locationState);
  if (replace) {
    return {
      path,
      history: { entries: entries.map((current, i) => (i === index ? entry : current)), index },
    };
  }

  // Keep the newest entries only; back history is in-memory and would otherwise grow for the
  // whole session (entries can carry dialog state).
  const nextEntries = [...entries.slice(0, index + 1), entry].slice(-MAX_WINDOW_HISTORY_ENTRIES);
  return {
    path,
    history: { entries: nextEntries, index: nextEntries.length - 1 },
  };
};

const navigateWindow = <RouteState extends DesktopRouteState = DesktopRouteState>(
  state: DesktopState<RouteState>,
  {
    id,
    path,
    replace,
    state: locationState,
    tabId,
  }: { id: string; path: string; state?: unknown; replace?: boolean; tabId?: string },
): DesktopState<RouteState> => {
  const target = state.windows.find((windowState) => windowState.id === id);
  if (!target) {
    return state;
  }

  return updateWindow(
    state,
    id,
    {
      ...getNavigationUpdates(
        tabId ? (getWindowTabs(target).find(({ id }) => id === tabId) ?? target) : target,
        path,
        locationState,
        replace,
      ),
      updatedAt: new Date().toISOString(),
    },
    tabId,
  );
};

const goWindow = <RouteState extends DesktopRouteState = DesktopRouteState>(
  state: DesktopState<RouteState>,
  id: string,
  delta: number,
  tabId?: string,
): DesktopState<RouteState> => {
  const target = state.windows.find((windowState) => windowState.id === id);
  if (!target) {
    return state;
  }

  const tab = tabId ? getWindowTabs(target).find(({ id }) => id === tabId) : target;
  if (!tab) return state;
  const { entries, index } = tab.history;
  const nextIndex = Math.min(entries.length - 1, Math.max(0, index + delta));
  if (nextIndex === index) {
    return state;
  }

  return updateWindow(
    state,
    id,
    {
      path: entries[nextIndex].path,
      history: { entries, index: nextIndex },
      updatedAt: new Date().toISOString(),
    },
    tabId,
  );
};

const focusWindow = <RouteState extends DesktopRouteState = DesktopRouteState>(
  state: DesktopState<RouteState>,
  id: string,
  updates: Partial<DesktopWindowState<RouteState>> = {},
): DesktopState<RouteState> => {
  if (!state.windows.some((windowState) => windowState.id === id)) {
    return state;
  }

  return {
    ...state,
    windows: state.windows.map((windowState) =>
      windowState.id === id
        ? syncSelectedTab(windowState, { ...updates, zIndex: state.nextZIndex })
        : windowState,
    ),
    focusedWindowId: id,
    nextZIndex: state.nextZIndex + 1,
  };
};

const updateWindow = <RouteState extends DesktopRouteState = DesktopRouteState>(
  state: DesktopState<RouteState>,
  id: string,
  updates: Partial<DesktopWindowState<RouteState>>,
  tabId?: string,
): DesktopState<RouteState> => {
  if (!state.windows.some((windowState) => windowState.id === id)) {
    return state;
  }

  const windows = state.windows.map((windowState) =>
    windowState.id === id ? syncSelectedTab(windowState, updates, tabId) : windowState,
  );

  return {
    ...state,
    windows,
    focusedWindowId: getFocusedWindowId(windows, state.focusedWindowId),
  };
};

export const getFocusedWindowId = <RouteState extends DesktopRouteState = DesktopRouteState>(
  windows: DesktopWindowState<RouteState>[],
  currentFocusedWindowId: string | undefined,
) => {
  const currentFocusedWindow = windows.find(
    (windowState) =>
      windowState.id === currentFocusedWindowId && windowState.isOpen && !windowState.isMinimized,
  );
  if (currentFocusedWindow) {
    return currentFocusedWindow.id;
  }

  return [...windows]
    .filter((windowState) => windowState.isOpen && !windowState.isMinimized)
    .sort((a, b) => b.zIndex - a.zIndex)[0]?.id;
};

const toggleMaximize = <RouteState extends DesktopRouteState = DesktopRouteState>(
  state: DesktopState<RouteState>,
  id: string,
): DesktopState<RouteState> => {
  const target = state.windows.find((windowState) => windowState.id === id);
  if (!target) {
    return state;
  }

  return focusWindow(state, id, {
    isOpen: true,
    isMinimized: false,
    isMaximized: !target.isMaximized,
    updatedAt: new Date().toISOString(),
  });
};
